import { request, withQuery } from './client';
import type {
  DashboardData,
  CardBankReport,
  DebtorsReport,
  HomeSummary,
  PaymentsReport,
  ReceiptList,
  ReconciliationAct,
  ReportGroup,
  ReportPeriod,
  SalesByPeriodReport,
  SalesReport,
  StockMovementReport,
  StockReport,
  VatReport,
} from '@kassa/shared';

export interface ReportQuery {
  period?: ReportPeriod;
  from?: string;
  to?: string;
  outletId?: string;
}

// Excel: sessiya cookie-si ilə blob kimi yüklənir, sonra brauzer yükləmə başladır
export async function downloadReport(path: string, params: Record<string, string | undefined>, format: 'xlsx' | 'pdf' = 'xlsx'): Promise<void> {
  const res = await fetch(withQuery(path, { ...params, format }), { credentials: 'same-origin' });
  if (!res.ok) throw new Error(format === 'pdf' ? 'PDF faylı yüklənmədi' : 'Excel faylı yüklənmədi');
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `${path.split('/').pop()}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Fayl (Excel) sessiya cookie-si ilə; xəta olarsa istisna (səhv fayl yüklənməsin). KT-99 «Yüklə ▾». */
export async function downloadFile(url: string, name: string): Promise<void> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Fayl yüklənmədi');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export const reportsApi = {
  reportDashboard: (outletId?: string) => request<DashboardData>('GET', withQuery('/api/reports/dashboard', { outletId })),
  reportHome: (q: ReportQuery) => request<HomeSummary>('GET', withQuery('/api/reports/home', q as Record<string, string>)),
  reportSales: (q: ReportQuery) => request<SalesReport>('GET', withQuery('/api/reports/sales', q as Record<string, string>)),
  reportDebtors: () => request<DebtorsReport>('GET', '/api/reports/debtors'),
  reportReceipts: (q: ReportQuery & { q?: string; cancelled?: '1' }) => request<ReceiptList>('GET', withQuery('/api/reports/receipts', q as Record<string, string>)),
  reportVat: (q: ReportQuery) => request<VatReport>('GET', withQuery('/api/reports/vat', q as Record<string, string>)),
  reportStock: (outletId?: string) => request<StockReport>('GET', withQuery('/api/reports/stock', { outletId })),
  // KT-90
  reportPayments: (q: ReportQuery & { registerId?: string; shiftId?: string }) =>
    request<PaymentsReport>('GET', withQuery('/api/reports/payments', q as Record<string, string>)),
  reportSalesByPeriod: (q: ReportQuery & { group: ReportGroup }) =>
    request<SalesByPeriodReport>('GET', withQuery('/api/reports/sales-by-period', { ...q })),
  reportStockMovement: (q: { from: string; to: string; outletId?: string; productId?: string }) =>
    request<StockMovementReport>('GET', withQuery('/api/reports/stock-movement', q)),
  reportCardBank: (q: ReportQuery) => request<CardBankReport>('GET', withQuery('/api/reports/card-bank', { ...q })),
  reportReconciliation: (q: { from: string; to: string; counterpartyId: string }) =>
    request<ReconciliationAct>('GET', withQuery('/api/reports/reconciliation', q)),
};
